import { lazy, Suspense } from 'react';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { API, ROUTES } from "@/config/constants";
import { AuthProvider } from "@/features/auth/AuthProvider";
import { DefaultAuthenticatedRoute, PlatformRoute, ProtectedRoute, PublicOnlyRoute } from "@/features/auth/ProtectedRoute";
import { AppLayout } from "@/components/layout/AppLayout";
import { PlatformLayout } from "@/components/platform/PlatformLayout";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { SkeletonPage } from "@/components/shared/SkeletonPage";

// ─── Lazy-loaded pages (route-level code splitting) ───────
// Auth Pages
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const ForgotPasswordPage = lazy(() => import("@/pages/auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/pages/auth/ResetPasswordPage"));
const AcceptInvitePage = lazy(() => import("@/pages/auth/AcceptInvitePage"));
const InvitationExpiredPage = lazy(() => import("@/pages/auth/InvitationExpiredPage"));
const UnauthorizedPage = lazy(() => import("@/pages/auth/UnauthorizedPage"));
const PlatformOrganizationsPage = lazy(() => import("@/pages/platform/OrganizationsPage"));

// App Pages
const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const OrganizationSettingsPage = lazy(() => import("@/pages/organization/OrganizationSettingsPage"));
const TenantsPage = lazy(() => import("@/pages/organization/TenantsPage"));
const PoliciesPage = lazy(() => import("@/pages/organization/PoliciesPage"));
const SettingsPage = lazy(() => import("@/pages/organization/SettingsPage"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const HealthPage = lazy(() => import("@/pages/HealthPage"));

// Employee Pages
const EmployeesPage = lazy(() => import("@/pages/employees/EmployeesPage"));
const EmployeeCreatePage = lazy(() => import("@/pages/employees/EmployeeCreatePage"));
const EmployeeEditPage = lazy(() => import("@/pages/employees/EmployeeEditPage"));
const EmployeeDetailPage = lazy(() => import("@/pages/employees/EmployeeDetailPage"));
const DocumentsPage = lazy(() => import("@/pages/documents/DocumentsPage"));
const ContractsPage = lazy(() => import("@/pages/contracts/ContractsPage"));
const AttendancePage = lazy(() => import("@/pages/attendance/AttendancePage"));
const MyTimePage = lazy(() => import("@/pages/attendance/MyTimePage"));
const ReviewsPage = lazy(() => import("@/pages/reviews/ReviewsPage"));

// Self-service Pages
const MyProfilePage = lazy(() => import("@/pages/self-service/MyProfilePage"));
const MyDocumentsPage = lazy(() => import("@/pages/self-service/MyDocumentsPage"));
const MyContractsPage = lazy(() => import("@/pages/self-service/MyContractsPage"));
const EmployeeSettingsPage = lazy(() => import("@/pages/self-service/EmployeeSettingsPage"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: API.STALE_TIME_MS,
      gcTime: API.CACHE_TIME_MS,
      retry: API.RETRY_COUNT,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

function PageFallback() {
  return <SkeletonPage />;
}

function App() {
  return (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageFallback />}>
            <Routes>
              {/* Public auth routes */}
              <Route path={ROUTES.LOGIN} element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />
              <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
              <Route path={ROUTES.RESET_PASSWORD} element={<ResetPasswordPage />} />
              <Route path={ROUTES.ACCEPT_INVITE} element={<AcceptInvitePage />} />
              <Route path="/invitation-expired" element={<InvitationExpiredPage />} />
              <Route path={ROUTES.UNAUTHORIZED} element={<UnauthorizedPage />} />
              {/* Keep the health route public so CI and production smoke checks stay unauthenticated. */}
              <Route path="/health" element={<HealthPage />} />

              <Route element={<PlatformRoute />}>
                <Route element={<PlatformLayout />}>
                  <Route path={ROUTES.PLATFORM_ROOT} element={<Navigate to={ROUTES.PLATFORM_ORGANIZATIONS} replace />} />
                  <Route path={ROUTES.PLATFORM_ORGANIZATIONS} element={<PlatformOrganizationsPage />} />
                </Route>
              </Route>

              <Route element={<ProtectedRoute requireClientAccess />}>
                <Route element={<AppLayout />}>
                  <Route path={ROUTES.DASHBOARD} element={<DashboardPage />} />

                  {/* Employee self-service routes */}
                  <Route path={ROUTES.MY_PROFILE} element={<MyProfilePage />} />
                  <Route path={ROUTES.MY_TIME} element={<MyTimePage />} />
                  <Route path={ROUTES.MY_DOCUMENTS} element={<MyDocumentsPage />} />
                  <Route path={ROUTES.MY_CONTRACTS} element={<MyContractsPage />} />
                  <Route path={ROUTES.MY_SETTINGS} element={<EmployeeSettingsPage />} />

                  <Route element={<ProtectedRoute requiredRole="admin" requireClientAccess />}>
                    {/* Admin routes — Employees */}
                    <Route path={ROUTES.EMPLOYEES} element={<EmployeesPage />} />
                    <Route path={ROUTES.EMPLOYEE_CREATE} element={<EmployeeCreatePage />} />
                    <Route path="/employees/:id/edit" element={<EmployeeEditPage />} />
                    <Route path={ROUTES.EMPLOYEE_DETAIL} element={<EmployeeDetailPage />} />

                    {/* Admin routes — Organization */}
                    <Route path={ROUTES.ORGANIZATION} element={<OrganizationSettingsPage />} />
                    <Route path={ROUTES.TENANTS} element={<TenantsPage />} />
                    <Route path={ROUTES.POLICIES} element={<PoliciesPage />} />
                    <Route path={ROUTES.SETTINGS} element={<SettingsPage />} />

                    {/* Admin routes — modules */}
                    <Route path={ROUTES.INVITATIONS} element={<PlaceholderPage title="Invitations" />} />
                    <Route path={ROUTES.DOCUMENTS} element={<DocumentsPage />} />
                    <Route path={ROUTES.CONTRACTS} element={<ContractsPage />} />
                    <Route path="/contracts/:id" element={<ContractsPage />} />
                    <Route path={ROUTES.ATTENDANCE_ADMIN} element={<AttendancePage />} />
                    <Route path={ROUTES.REVIEWS} element={<ReviewsPage />} />
                    <Route path={ROUTES.AUDIT_LOG} element={<PlaceholderPage title="Audit Log" />} />
                  </Route>
                </Route>
              </Route>

              {/* Root redirect */}
              <Route path="/" element={<DefaultAuthenticatedRoute />} />

              {/* 404 */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
  </ErrorBoundary>
  );
}


// Temporary placeholder for routes not yet built
function PlaceholderPage({ title }: { title: string }) {
  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <h1 className="text-2xl font-semibold text-foreground">{title}</h1>
      <p className="text-sm text-muted-foreground mt-1">This module will be available in an upcoming phase.</p>
      <div className="mt-6 rounded-xl border border-dashed border-border p-12 text-center text-muted-foreground">
        Coming soon
      </div>
    </div>
  );
}

export default App;
